// Apply a saved drip template (FE#331). Lists the user's `drip-templates`
// (each a saved sequence: messages + the gaps between them), previews the
// steps + gaps, and on pick spins up a fresh DRAFT drip seeded with that
// sequence — then routes into the normal wizard at the AUDIENCE step, where the
// user chooses the target group/audience + start date. Reuses the drip model
// 1:1; no scheduling logic here.
import React from 'react';
import { ArrowLeft, Bookmark, Layers, Trash2, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common/config/constants';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import {
  DBDripTemplateThunks,
  DBDripThunks,
  DRIP_TEMPLATE_LIST_QUERY,
  dripTemplatesSelectByQuery,
} from '@common-reducers/DBServiceThunks';
import { seedDraftDripFromTemplate } from '@common-reducers/drip-template-helpers';
import type { DripTemplate } from '@common/models/drip-template';
import type { Drip } from '@bt-common/schemas/bt/drip';
import { gapText, type DelayGap } from './primitives';

interface DripTemplatePickerProps {
  onBack: () => void;
}

export const DripTemplatePicker: React.FC<DripTemplatePickerProps> = ({ onBack }) => {
  const { t } = useTranslation(namespace);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [applyingId, setApplyingId] = React.useState<string | null>(null);

  React.useEffect(() => {
    dispatch(DBDripTemplateThunks.find({ deleted: { $ne: true }, ...DRIP_TEMPLATE_LIST_QUERY }));
  }, [dispatch]);

  const templates = useAppSelector((s) =>
    dripTemplatesSelectByQuery(s.DBDripTemplateReducer, { deleted: { $ne: true } }, [], { updatedAt: -1 }),
  ) as DripTemplate[];
  const loaded = useAppSelector((s) => s.DBDripTemplateReducer.itemsLoaded);

  const apply = async (tpl: DripTemplate) => {
    if (applyingId) return;
    setApplyingId(String(tpl._id ?? ''));
    try {
      const draft = seedDraftDripFromTemplate(tpl);
      const created = await dispatch(DBDripThunks.create(draft as Drip)).unwrap();
      const newId = created?._id;
      if (typeof newId !== 'string') throw new Error('Drip create returned no id');
      navigate(`/campaigns/${newId}/edit/audience`);
    } catch (err) {
      console.error('[DripTemplatePicker] apply failed', err);
      setApplyingId(null);
    }
  };

  const remove = (tpl: DripTemplate, e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(DBDripTemplateThunks.patch({ entity: { ...tpl, deleted: true } }));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderBottom: '1px solid var(--line)' }}>
        <button type="button" className="btn ghost" onClick={onBack} style={{ padding: 6 }} aria-label={t('common.back', 'Back')}>
          <ArrowLeft size={16} />
        </button>
        <div>
          <h1 style={{ fontSize: 17, fontWeight: 600, letterSpacing: '-0.01em', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Bookmark size={16} /> {t('blueticksPanel.dripTemplates.pickerTitle', 'Start from a drip template')}
          </h1>
          <p style={{ margin: '3px 0 0', fontSize: 12.5, color: 'var(--muted)' }}>
            {t('blueticksPanel.dripTemplates.pickerSubtitle', "Pick a saved sequence. We'll create a new drip with the same messages and gaps — you choose the audience and start date next.")}
          </p>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: 18 }}>
        {loaded && templates.length === 0 && (
          <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--muted)' }}>
            <Bookmark size={28} style={{ opacity: 0.5 }} />
            <h3 style={{ margin: '12px 0 4px', fontSize: 14, color: 'var(--ink)' }}>
              {t('blueticksPanel.dripTemplates.pickerEmptyHeadline', 'No drip templates yet')}
            </h3>
            <p style={{ margin: 0, fontSize: 12.5 }}>
              {t('blueticksPanel.dripTemplates.pickerEmptyHint', 'Open a drip’s menu and choose “Save as template” to reuse its sequence here.')}
            </p>
          </div>
        )}

        <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', maxWidth: 1040, margin: '0 auto' }}>
          {templates.map((tpl) => {
            const steps = tpl.steps ?? [];
            const id = String(tpl._id ?? '');
            const isApplying = applyingId === id;
            return (
              <div
                key={id}
                role="button"
                tabIndex={0}
                onClick={() => void apply(tpl)}
                onKeyDown={(e) => { if (e.key === 'Enter') void apply(tpl); }}
                style={{
                  border: '1px solid var(--line)', borderRadius: 12, background: 'var(--surface)',
                  padding: 14, cursor: isApplying ? 'progress' : 'pointer', opacity: isApplying ? 0.6 : 1,
                  display: 'flex', flexDirection: 'column', gap: 10, transition: 'border-color .12s, box-shadow .12s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--line-strong)'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(11,18,32,.06)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--line)'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                  <strong style={{ fontSize: 13.5, letterSpacing: '-0.01em', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {tpl.name || t('blueticksPanel.dripTemplates.untitled', 'Untitled template')}
                  </strong>
                  <button
                    type="button"
                    className="btn ghost"
                    onClick={(e) => remove(tpl, e)}
                    aria-label={t('common.delete', 'Delete')}
                    style={{ padding: 3, flexShrink: 0 }}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>

                <span className="pill brand" style={{ alignSelf: 'flex-start', fontSize: 10, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <Layers size={11} /> {/* The SAME key the wizard's stepper and its Review use: a template's
                      "3 messages" and a campaign's "3 messages" are one concept, and
                      two keys for it is how they drift apart in translation. */}
                  {t('campaignsRedesign.counts.messages', { count: steps.length })}
                </span>

                <ol style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {steps.slice(0, 4).map((s, i) => (
                    <li key={i} style={{ fontSize: 11.5, color: 'var(--muted)', display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <span style={{ color: 'var(--ink-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {i + 1}. {(s.template as { message?: string })?.message?.trim() || t('blueticksPanel.dripTemplates.mediaStep', 'Media / poll message')}
                      </span>
                      {s.gapAfter && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginInlineStart: 12 }}>
                          <Clock size={10} /> {t('blueticksPanel.dripTemplates.thenWait', 'then wait {{gap}}', { gap: gapText(t, s.gapAfter as DelayGap) })}
                        </span>
                      )}
                    </li>
                  ))}
                  {steps.length > 4 && (
                    <li style={{ fontSize: 11, color: 'var(--muted-2)' }}>+{steps.length - 4}…</li>
                  )}
                </ol>

                <button type="button" className="btn primary" disabled={isApplying} onClick={(e) => { e.stopPropagation(); void apply(tpl); }} style={{ marginTop: 'auto' }}>
                  {t('blueticksPanel.dripTemplates.use', 'Use template')}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default DripTemplatePicker;
